import type { ID, ResourceResponse, ThunkReturnType } from "types";
import { ApiRouteTypes } from "consts/enpoints/api";
import {
    useResetState,
    useExportTable,
    useRequest,
    type RequestSettings
} from "hooks";
import type {
    RolesResponse,
    User,
    UserMerchantRequestParams,
    UserMorRequestParams,
    UserResponse,
    UsersMerchantsPivot,
    UsersMerchantsResponse,
    UsersMorsPivot,
    UsersMorsResponse,
    UsersResponse
} from "./types";
import {
    associateUserMerchantThunk,
    associateUserMorThunk,
    deleteUserThunk,
    disassociateUserMerchantThunk,
    disassociateUserMorThunk,
    getRolesThunk,
    getUserByIdThunk,
    getUsersMerchantsThunk,
    getUsersMorsThunk,
    getUsersThunk,
    postUserThunk
} from "./thunks";
import { resetState, changeExportLoading } from "./slice";

const useActions = () => {
    const getUsers = useRequest<ThunkReturnType<UsersResponse>, string | undefined>({
        thunk: getUsersThunk
    });

    const getUserById = useRequest<ThunkReturnType<UserResponse>, ID>({
        thunk: getUserByIdThunk
    });

    const getRoles = useRequest<
        ThunkReturnType<RolesResponse>,
        string | undefined
    >({
        thunk: getRolesThunk
    });

    const getUsersMerchants = useRequest<ThunkReturnType<UsersMerchantsResponse>, ID | undefined>({
        thunk: getUsersMerchantsThunk
    });

    const getUsersMors = useRequest<ThunkReturnType<UsersMorsResponse>, ID | undefined>({
        thunk: getUsersMorsThunk
    });

    const deleteUser = useRequest<ResourceResponse<ID>, ID>({
        thunk: deleteUserThunk
    });

    const associateUserMerchant = useRequest<
        ThunkReturnType<ResourceResponse<UsersMerchantsPivot>>,
        UserMerchantRequestParams
    >({
        thunk: associateUserMerchantThunk
    });

    const disassociateUserMerchant = useRequest<
        ThunkReturnType<ResourceResponse<null>>,
        UserMerchantRequestParams
    >({
        thunk: disassociateUserMerchantThunk
    });

    const associateUserMor = useRequest<
        ThunkReturnType<ResourceResponse<UsersMorsPivot>>,
        UserMorRequestParams
    >({
        thunk: associateUserMorThunk
    });

    const disassociateUserMor = useRequest<
        ThunkReturnType<ResourceResponse<null>>,
        UserMorRequestParams
    >({
        thunk: disassociateUserMorThunk
    });

    const updateOrCreate = useRequest<any, Partial<User>>({
        thunk: postUserThunk
    });

    const downloadCsv = useExportTable({
        changeExportLoading,
        apiResourceType: ApiRouteTypes.GetUsers
    });

    const resetMerchants = useResetState(resetState);

    const updateOrCreateUser = async (
        userRequest: Partial<User>,
        requestSettings?: RequestSettings
    ): Promise<ResourceResponse<User>> => {
        const [request] = updateOrCreate(userRequest, requestSettings);

        return (await request);
    };

    return {
        getUsers,
        getUserById,
        deleteUser,
        getRoles,
        getUsersMerchants,
        getUsersMors,
        associateUserMerchant,
        disassociateUserMerchant,
        associateUserMor,
        disassociateUserMor,
        updateOrCreateUser,
        downloadCsv,
        resetMerchants
    };
};

export default useActions;
